import HeroFirstFrame from '@c/headers/homepage/HeroFirstFrame';
import { DefaultLayout } from '@c/layouts';
import PopularCategories from '@c/PopularCategories';
import TrustedBrands from '@c/marketplace/TrustedBrands';
import LiveActivityBar from '@c/marketplace/LiveActivityBar';
import FeaturedListings from '@c/marketplace/FeaturedListings';
import PageSectionHeader from '@ui/PageSectionHeader';
import {
  getAuctions,
  getDealsAndSteals,
  getFeaturedProducts
} from '@util/firestore/products';
import { BlogDocument } from '@util/types/firestore/blog';
import { ProductDocument } from '@util/types/firestore/products';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import CEOMessage from '@c/headers/homepage/CEOMessage';
import { useMediaQuery } from '@util/hooks/useMediaQuery';
import { useMemo, useEffect, useState } from 'react';
import SEO from '@c/SEO';
import { WebsiteSchema, OrganizationSchema } from '@c/StructuredData';

const BlogLink = dynamic(() => import('@ui/Link/BlogLink'), {
  ssr: false,
});

// The hero carousels never server-render (GB #1486). HeroFirstFrame is what the
// server sends; each carousel library — react-slick on desktop, Swiper on
// mobile — is downloaded only on the breakpoint that actually uses it, instead
// of every phone downloading the desktop carousel's markup and banner.
//
// `loading` matters as much as `ssr: false`: without it, next/dynamic renders
// nothing while the chunk downloads, which would collapse the hero box for a
// moment and reintroduce exactly the layout shift this work removes. Holding
// the static frame until the carousel is ready keeps the box occupied the whole
// time.
const HomepageHeader = dynamic(
  () => import('@c/headers/homepage/HomepageHeader'),
  { ssr: false, loading: () => <HeroFirstFrame /> }
);
const MobileHero = dynamic(() => import('@c/headers/homepage/MobileHero'), {
  ssr: false,
  loading: () => <HeroFirstFrame />,
});

// Renders the static server frame until the client knows its real viewport,
// then mounts ONLY the carousel for the active breakpoint. The `mounted` gate
// keeps the first client render byte-identical to the server HTML, so there is
// no hydration mismatch, and the swap happens inside an identically sized box,
// so there is no layout shift. Slide 1 of the mounting carousel reuses the same
// image URL the static frame already loaded, so it is served from cache rather
// than refetched.
const HomeHero = () => {
  const [mounted, setMounted] = useState(false);
  // (width < 48rem), not a hardcoded px value: Tailwind v4's md: breakpoint
  // compiles to @media (width >= 48rem) (verified against this repo's own
  // compiled globals.css), and rem inside a media query resolves against the
  // browser's INITIAL/default font size, not any html{font-size} override
  // elsewhere in this file. A hardcoded px boundary (e.g. 767.98px) only
  // agrees with 48rem when that default is exactly 16px; at other browser
  // font sizes (e.g. 20px, common for accessibility users) the two boundaries
  // land at different viewport widths, so the CSS and this query can disagree
  // — the ticker/hero CSS order and the JS-mounted carousel choose different
  // breakpoints, producing a layout shift. Matching the CSS's own unit removes
  // that band entirely, for every browser default font size, not just 16px.
  const isMobile = useMediaQuery('(width < 48rem)');
  useEffect(() => setMounted(true), []);
  if (!mounted) return <HeroFirstFrame />;
  return isMobile ? <MobileHero /> : <HomepageHeader />;
};
import Carousel from '@ui/Carousel';
import MarketplaceProductCard, {
  CAROUSEL_CARD_WIDTH,
} from '@c/marketplace/MarketplaceProductCard';

// Feeds the Deals and Steals and Current auctions carousels.
// MarketplaceProductCard is already memoised, so no extra wrapper is needed.
const renderProductCards = (productItems: ProductDocument[]) =>
  productItems.map((item, i) => (
    <MarketplaceProductCard
      item={item}
      key={item.sku + '-' + i}
      className={CAROUSEL_CARD_WIDTH}
    />
  ));

function Home() {
  const [featuredListings, setFeaturedListings] = useState<ProductDocument[]>([]);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [dealsAndSteals, setDealsAndSteals] = useState<ProductDocument[]>([]);
  // Auctions carousel is a static, page-load snapshot of the 10 soonest-ending
  // live auctions, matching the featured/deals fetch pattern below. It used to
  // open a Firestore onSnapshot listener for EVERY homepage visitor (incl.
  // anonymous) — ~1.27MB, the single largest homepage item (web#1795).
  // Trade-off vs the old live listener: an auction that sells or ends while a
  // visitor sits on the homepage is not live-removed from the set until reload.
  // This is acceptable here — each card's own countdown (useCountdown, which
  // refetches on countdown-zero) and 10s bid poll keep the timer and price
  // fresh, anonymous visitors can't bid, and click-through shows true live
  // state via useRealtimeBids on the listing page.
  const [auctions, setAuctions] = useState<ProductDocument[]>([]);
  const [recentBlogPosts, setRecentBlogPosts] = useState<Partial<BlogDocument>[]>([]);

  useEffect(() => {
    getFeaturedProducts(12)
      .then((res) => {
        if (res?.results) setFeaturedListings(res.results);
      })
      // Settle loading either way — a rejected fetch must not leave skeletons forever.
      .catch(() => undefined)
      .finally(() => setFeaturedLoading(false));

    // Same .catch discipline as the featured fetch above: these had none, so a
    // rejected request became an unhandled promise rejection. The sections
    // degrade to empty on failure, which is the intended behaviour — they just
    // must not do it by throwing.
    getDealsAndSteals(12)
      .then((res) => {
        if (res?.results) setDealsAndSteals(res.results);
      })
      .catch(() => undefined);

    // 10 soonest-ending live auctions, fetched once (see the auctions state
    // comment above). Same .catch discipline: degrade to an empty carousel on
    // failure rather than throwing an unhandled rejection.
    getAuctions(10)
      .then((res) => {
        if (res?.results) setAuctions(res.results);
      })
      .catch(() => undefined);

    // Goes through our own API route rather than calling WordPress directly.
    // The direct call required WordPress credentials, which Next.js inlined
    // into this page's browser bundle. See GearBoard #1683.
    fetch('/api/blog/recent?n=3')
      .then((response) => (response.ok ? response.json() : { results: [] }))
      .then((res) => {
        if (res?.results) setRecentBlogPosts(res.results);
      })
      // getNBlogs() caught internally and could never reject; fetch can (offline,
      // DNS, navigation abort). Without this the homepage gets an unhandled
      // promise rejection where it previously just rendered an empty section.
      .catch(() => setRecentBlogPosts([]));
  }, []);

  const memoizedDealsCards = useMemo(() =>
    renderProductCards(dealsAndSteals), [dealsAndSteals]);
  const memoizedAuctionCards = useMemo(() =>
    auctions.length > 0
      ? renderProductCards(auctions)
      : [],
    [auctions]);
  return (
    <div className="w-full">
      <SEO
        title="Buy & Sell Used Camera Gear"
        description="Buy and sell used camera gear from verified sellers on GearFocus. Free listings, low fees, real-item photos, and a 48-hour verification period."
        ogImage="/images/og-home.jpg"
        keywords={[
          'camera gear', 'photography equipment', 'used cameras', 'used lenses',
          'professional cameras', 'Canon', 'Nikon', 'Sony', 'DSLR', 'mirrorless'
        ]}
      />

      {/* Bespoke media-gated LCP preloads (GB #1486 / P1.1, site-speed plan
          2026-07-17). Each link preloads slide 1 of the hero for ONE breakpoint
          only; the media attribute is what stops phones from downloading the
          desktop banner and desktops from downloading the mobile one. These
          hrefs must stay in lockstep with HeroFirstFrame's <picture>, with
          banners[0] of both carousels, and with the one breakpoint boundary
          this page uses everywhere: (width < 48rem) for mobile against
          Tailwind's md: (width >= 48rem), the same unit Tailwind v4 itself
          compiles md: to. HomeHero's useMediaQuery call carries the same
          48rem boundary for that reason — see the comment there for why a
          rem boundary, not a hardcoded px one, is required.
          `fetchpriority="high"` supplies the priority signal that next/image's
          `priority` prop would otherwise inject WITHOUT a media gate, which is
          why `priority` must never come back onto a hero image.
          COUPLING TO PHASE 2 (Bunny image transforms): if these image URLs are
          rewritten, update HeroFirstFrame.tsx in the same change — otherwise
          the LCP image double-downloads, the preloads spending bytes on the old
          URLs while the markup fetches the new ones. */}
      <Head>
        {/* fetchPriority isn't in this repo's LinkHTMLAttributes typings
            (it IS on ImgHTMLAttributes, which is why UIProductCard.tsx's
            <img fetchPriority={...}> elsewhere needs no cast) — spread as
            `any` rather than widen the shared React DOM types. */}
        <link
          rel="preload"
          as="image"
          href="/banners/mobile-banner-2.webp"
          media="(width < 48rem)"
          {...({ fetchpriority: 'high' } as any)}
        />
        <link
          rel="preload"
          as="image"
          href="/banners/Gearfocus_WebsiteHeaders2.webp"
          media="(width >= 48rem)"
          {...({ fetchpriority: 'high' } as any)}
        />
      </Head>

      <WebsiteSchema />
      <OrganizationSchema />
      {/* VideoObjectSchema removed with the video banner below it. The schema
          asserts a video is present on this page; leaving it after the only
          embed is gone would be structured data describing something that
          isn't here. If the video returns in a restyled, modal-gated form,
          this comes back with it. */}

      {/* Ticker and hero swap places by breakpoint: the ticker is pinned to the
          very top on mobile, and sits flush under the hero on desktop where it
          then sticks just below the menu. That used to be two JSX branches
          chosen by JavaScript, so the ticker was mounted from whichever branch
          won — and any naive "render both" fix would mount it TWICE and fire
          its Firestore fetch twice. Making THIS container the flex column and
          giving only these two children negative `order` values puts a SINGLE
          ticker in the right place at each breakpoint with CSS alone, correct
          in the server HTML with no JavaScript involved.

          Why the order values are negative, and why the flex column is this
          element rather than a small wrapper around the two of them: a sticky
          element can only stay pinned while its PARENT is on screen. The
          desktop ticker is meant to stay under the menu for the whole page, so
          its parent must remain this full-height container. Wrapping just the
          hero and ticker in their own short box was measured to unpin the
          ticker as soon as the hero scrolled past. Negative orders keep every
          other child at the default order of 0, so nothing else on the page
          needed to change. */}
      <div className="relative flex w-full select-none flex-col">
        {/* Sticky is applied here as classes rather than through the
            component's `sticky` prop, because that prop also sets an inline
            `top` which would win over any breakpoint gate. As classes, mobile
            stays static and only md+ becomes sticky. -mt-px covers the
            sub-pixel seam below the hero on desktop, where the ticker sits
            underneath it.

            Desktop DOM order: this component (the ticker) is first in markup
            on BOTH breakpoints; only the CSS `order` utilities below paint
            the hero first on desktop. That differs from main, where desktop
            renders <HomepageHeader/> before <LiveActivityBar/> in the DOM, so
            a keyboard/screen-reader user on desktop now reaches ticker links
            before the hero's CTA. Visual order matches main at both
            breakpoints; DOM/focus order does not on desktop. Accepted as
            implemented per Bill's ruling, 2026-09-06: "Accept desktop order;
            apply the boundary fix; iPhone check; merge." */}
        <LiveActivityBar className="order-[-2] md:order-[-1] md:sticky md:top-[var(--gf-header-h)] md:z-40 md:-mt-px" />
        <div className="order-[-1] md:order-[-2]">
          <HomeHero />
        </div>

        <FeaturedListings items={featuredListings} loading={featuredLoading} />

        {/* Brand marquee moved below Featured (both breakpoints). */}
        <TrustedBrands />

        {/* No sm:mt here (Bill, 2026-08-22 — was sm:mt-[4rem], adding a
            fourth, uncoordinated 64px on top of TrustedBrands' own bottom
            padding above popular-categories, its first child in the common
            case with no live auctions. TrustedBrands.tsx's own pt/pb are now
            the sole source of every gap between it and its neighbors, at
            every breakpoint — see the comment there. */}
        <div className="mx-auto flex w-full max-w-default flex-col gap-[1rem] overflow-clip px-4 pb-16 sm:gap-[3rem] sm:pb-24 lg:px-[40px] xl:px-default-padding-x">
          {/* Section ids. The homepage had none, so there was nothing on it to
              point a `/#fragment` at — which is why the scroll fix could not be
              told apart from the bug it fixes.

              Use #popular-categories as the anchor test target. It is the only
              section here whose position is stable at hash-scroll time: its
              tiles are a hardcoded list, so it renders at final height on the
              first paint. Featured, Deals and Auctions all populate from client
              fetches in the effect above and grow after landing, which moves
              anything anchored below them and re-muddies exactly the
              measurement this is here to settle. */}
          {auctions.length > 0 && (
            <div id="current-auctions">
              <PageSectionHeader
                title="Current auctions"
                href="/collections/auctions"
                linkText="See All"
              />
              <Carousel items={memoizedAuctionCards} />
            </div>
          )}

          <div id="deals-and-steals" className="pb-4 sm:pb-0">
            <PageSectionHeader
              title="Deals and Steals"
              href="/collections/deals-and-steals"
              linkTitle="Browse Our Best Deals on Camera Equipment"
              linkText="See All"
            />
            <Carousel items={memoizedDealsCards} />
          </div>

          <div id="popular-categories">
            <PageSectionHeader title="Popular Categories" />
            <PopularCategories />
          </div>

          {recentBlogPosts?.length > 0 && (
            <div id="from-the-blog" className="px-4 pb-8 sm:px-0 sm:pb-4">
              <PageSectionHeader
                title="From the GearFocus Blog"
                href="/blog"
                linkTitle="Browse Our Best Deals on Camera Equipment"
                linkText="Explore the Blog"
              />
              <div className="flex gap-[2.4rem] overflow-x-auto">
                {recentBlogPosts.map((post) => (
                  <BlogLink key={post.id} blog={post} hideMetaOnMobile />
                ))}
              </div>
            </div>
          )}

          <CEOMessage />
        </div>
      </div>
    </div>
  );
}

Home.getLayout = function getLayout(page: React.ReactElement) {
  return <DefaultLayout>{page}</DefaultLayout>;
};

export default Home;
